/**
 * ImageLab - App Component
 * Main application component with React Router
 */

import { useCallback, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Header, Footer, DropZone, CodeDisplay, Preview } from "./components";
import { AdUnit } from "./components/AdUnit";
import { HowItWorks, Features } from "./components/ContentSections";
import {
  SvgToCodeEducational,
  PngToSvgEducational,
  FaviconEducational,
  ImageCompressorEducational,
} from "./components/EducationalContent";
import { PngToSvgConverter } from "./components/PngToSvgConverter";
import { FaviconGenerator } from "./components/FaviconGenerator";
import { ImageCompressor } from "./components/ImageCompressor";
import { ImageConverter } from "./components/ImageConverter";
import { SvgEditor } from "./components/SvgEditor";
import { SeoHead } from "./components/SeoHead";
import { AboutPage } from "./components/AboutPage";
import { FAQPage } from "./components/FAQPage";
import { TutorialsPage } from "./components/TutorialsPage";
import { ResourcesPage } from "./components/ResourcesPage";
import { PrivacyPolicy } from "./components/PrivacyPolicy";
import { useSvgProcessor, useKeyboardShortcuts, useClipboard } from "./hooks";
import { useToast } from "./context";
import { validateSvg } from "./services";
import { APP_CONFIG, MESSAGES } from "./config/constants";
import { getRouteByPath, ROUTES } from "./config/routes";

// SVG to Code page component
function SvgToCodePage() {
  const { state, processFile, processPastedCode } = useSvgProcessor();
  const { paste } = useClipboard();
  const { success, error, info } = useToast();

  const handleFileSelect = useCallback(
    async (file: File) => {
      info(MESSAGES.info.processing);
      const result = await processFile(file);
      if (result) {
        success(MESSAGES.success.converted);
      } else {
        error(MESSAGES.errors.invalidSvg);
      }
    },
    [processFile, success, error, info],
  );

  const handlePaste = useCallback(async () => {
    const text = await paste();
    if (!text) return;
    const validation = validateSvg(text);
    if (validation.valid) {
      processPastedCode(text);
      success(MESSAGES.success.pasted);
    }
  }, [paste, processPastedCode, success]);

  useKeyboardShortcuts([{ key: "v", ctrlKey: true, handler: handlePaste }]);

  return (
    <>
      {/* Tool Interface */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <section aria-label="Upload area" className="space-y-6">
          <DropZone
            acceptedTypes={APP_CONFIG.supportedFormats.svg}
            onFileSelect={handleFileSelect}
            hint={MESSAGES.info.dropHint}
            disabled={state.isProcessing}
          />
          <Preview svgCode={state.svgCode} fileName={state.currentFile?.name} />
        </section>
        <section aria-label="Code output">
          <CodeDisplay
            code={state.svgCode}
            fileName={state.currentFile?.name}
          />
        </section>
      </div>

      {/* Educational Content - Adds value for AdSense compliance */}
      <SvgToCodeEducational />

      {/* Ad Unit - Placed after substantial content */}
      {/* <div className="mb-8">
        <AdUnit adSlot="6615639453" className="rounded-lg overflow-hidden" />
      </div> */}
    </>
  );
}

// PNG to SVG page with educational content
function PngToSvgPage() {
  return (
    <>
      <PngToSvgConverter />
      <PngToSvgEducational />
      {/* <div className="mt-8">
        <AdUnit adSlot="6615639453" className="rounded-lg overflow-hidden" />
      </div> */}
    </>
  );
}

// Favicon Generator page with educational content
function FaviconGeneratorPage() {
  return (
    <>
      <FaviconGenerator />
      <FaviconEducational />
      {/* <div className="mt-8">
        <AdUnit adSlot="6615639453" className="rounded-lg overflow-hidden" />
      </div> */}
    </>
  );
}

// Image Compressor page with educational content
function ImageCompressorPage() {
  return (
    <>
      <ImageCompressor />
      <ImageCompressorEducational />
      {/* <div className="mt-8">
        <AdUnit adSlot="6615639453" className="rounded-lg overflow-hidden" />
      </div> */}
    </>
  );
}

// Layout wrapper with SEO and common elements
function AppLayout() {
  const location = useLocation();
  const route = getRouteByPath(location.pathname) || ROUTES[0];

  // Hide content sections on info pages (about, faq, privacy, tutorials, resources)
  const isInfoPage = [
    "/about",
    "/faq",
    "/privacy",
    // "/tutorials",
    "/resources",
  ].includes(location.pathname);

  // Prevent default drag behavior on body
  useEffect(() => {
    const preventDefault = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const events = ["dragenter", "dragover", "dragleave", "drop"];
    events.forEach((event) =>
      document.body.addEventListener(event, preventDefault),
    );
    return () => {
      events.forEach((event) =>
        document.body.removeEventListener(event, preventDefault),
      );
    };
  }, []);

  // Log initialization
  useEffect(() => {
    console.log(`${APP_CONFIG.name} v${APP_CONFIG.version} initialized`);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <SeoHead />

      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-indigo-600 text-white px-4 py-2 rounded-lg z-50"
      >
        Skip to main content
      </a>

      <Header />

      <main id="main-content" className="flex-1 py-8" role="main">
        <div className="max-w-7xl mx-auto px-4">
          {/* SEO-friendly heading */}
          <h2 className="sr-only">{route.title}</h2>

          <Routes>
            <Route path="/" element={<SvgToCodePage />} />
            <Route path="/png-to-svg" element={<PngToSvgPage />} />
            <Route
              path="/favicon-generator"
              element={<FaviconGeneratorPage />}
            />
            <Route path="/image-compressor" element={<ImageCompressorPage />} />
            <Route path="/image-converter" element={<ImageConverter />} />
            <Route path="/svg-editor" element={<SvgEditor />} />
            {/* <Route path="/tutorials" element={<TutorialsPage />} /> */}
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
          </Routes>
        </div>
      </main>

      {/* Content sections for AdSense compliance - hide on info pages */}
      {!isInfoPage && (
        <>
          <HowItWorks />
          <Features />
        </>
      )}

      <Footer />
    </div>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AppLayout />
    </BrowserRouter>
  );
}
